import handlebars from "handlebars";
import { generateDebtTemplate} from "../templates/debtTemplate";

// Define interfaces for the debt data
interface DebtData {
  lender?: string;
  interestRate?: string | number;
  maturity?: string;
  outstandingBalance?: number;
}

/**
 * Compiles debt data into HTML template for PDF generation
 * @param debtData The debt information to include in the PDF
 * @returns HTML string ready for PDF generation
 */
export const compileDebtTemplate = (debtData: DebtData): string => {
  const compileTemplate = handlebars.compile(generateDebtTemplate);

  // Format the interest rate to include % symbol if it's a number
  const formatInterestRate = (rate: string | number | undefined): string => {
    if (rate === undefined || rate === null) return '';
    if (typeof rate === 'number') {
      return `${rate.toFixed(2)}%`;
    }
    // If it's already a string, check if it includes % symbol
    return rate.includes('%') ? rate : `${rate}%`;
  };

  // Helper function to format dates
  const formatDebtDate = (dateString: string | undefined): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null) return '';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Transform the data to match the template structure
  const transformedData = {
    lender: debtData?.lender || '',
    interestRate: formatInterestRate(debtData?.interestRate),
    maturity: formatDebtDate(debtData?.maturity),
    outstandingBalance: formatCurrency(debtData?.outstandingBalance)
  };

  return compileTemplate(transformedData);
};
